import React from 'react'
import { Stack, Table, TableData, Text } from '@mantine/core'
import ReactECharts from 'echarts-for-react'
import { EChartsOption } from 'echarts'
import { useTheme } from '../../../components/ThemeProvider'
import { useViewMode } from '../ViewModeProvider'
import { getThemeSecondaryColor } from '../../../utils/themeConfig'

interface SpecialPeriodAnalysisChartProps {
  data: any
}

export default function SpecialPeriodAnalysisChart({ data }: SpecialPeriodAnalysisChartProps) {
  const theme = useTheme()
  const { viewMode } = useViewMode()

  const periods = [
    { label: '近7天', key: 'd7' },
    { label: '近14天', key: 'd14' },
    { label: '近1月', key: 'm1' },
    { label: '近3月', key: 'm3' },
    { label: '近6月', key: 'm6' },
    { label: '近12月', key: 'm12' },
  ]

  const periodsData = periods.map((p) => ({
    label: p.label,
    idWeekendCount: Number(data?.[`${p.key}_id_weekend_allnum`]) || 0,
    idWeekendOrg: Number(data?.[`${p.key}_id_weekend_orgnum`]) || 0,
    cellWeekendCount: Number(data?.[`${p.key}_cell_weekend_allnum`]) || 0,
    cellWeekendOrg: Number(data?.[`${p.key}_cell_weekend_orgnum`]) || 0,
    idNightCount: Number(data?.[`${p.key}_id_night_allnum`]) || 0,
    idNightOrg: Number(data?.[`${p.key}_id_night_orgnum`]) || 0,
    cellNightCount: Number(data?.[`${p.key}_cell_night_allnum`]) || 0,
    cellNightOrg: Number(data?.[`${p.key}_cell_night_orgnum`]) || 0,
  }))

  const hasData = periodsData.some(
    (p) =>
      p.idWeekendCount > 0 ||
      p.idWeekendOrg > 0 ||
      p.cellWeekendCount > 0 ||
      p.cellWeekendOrg > 0 ||
      p.idNightCount > 0 ||
      p.idNightOrg > 0 ||
      p.cellNightCount > 0 ||
      p.cellNightOrg > 0
  )

  if (!hasData) {
    return null
  }

  const weekendTableData: TableData = {
    head: ['时间段', '身份证申请次数', '身份证机构数', '手机号申请次数', '手机号机构数'],
    body: periodsData.map((p) => [p.label, p.idWeekendCount, p.idWeekendOrg, p.cellWeekendCount, p.cellWeekendOrg]),
  }

  const nightTableData: TableData = {
    head: ['时间段', '身份证申请次数', '身份证机构数', '手机号申请次数', '手机号机构数'],
    body: periodsData.map((p) => [p.label, p.idNightCount, p.idNightOrg, p.cellNightCount, p.cellNightOrg]),
  }

  const weekendOptions: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
    },
    legend: {
      data: ['身份证申请次数', '手机号申请次数'],
      bottom: 0,
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '10%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: periodsData.map((p) => p.label),
      axisLabel: {
        fontSize: 10,
      },
    },
    yAxis: {
      type: 'value',
      min: 0,
    },
    series: [
      {
        name: '身份证申请次数',
        type: 'bar',
        label: {
          show: true,
          color: '#ffffff',
          fontSize: 10,
        },
        itemStyle: {
          color: theme?.primaryColor || '#3B82F6',
        },
        data: periodsData.map((p) => p.idWeekendCount),
      },
      {
        name: '手机号申请次数',
        type: 'bar',
        label: {
          show: true,
          color: '#ffffff',
          fontSize: 10,
        },
        itemStyle: {
          color: getThemeSecondaryColor(theme?.type),
        },
        data: periodsData.map((p) => p.cellWeekendCount),
      },
    ],
  }

  const nightOptions: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
    },
    legend: {
      data: ['身份证申请次数', '手机号申请次数'],
      bottom: 0,
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '10%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: periodsData.map((p) => p.label),
      axisLabel: {
        fontSize: 10,
      },
    },
    yAxis: {
      type: 'value',
      min: 0,
    },
    series: [
      {
        name: '身份证申请次数',
        type: 'bar',
        label: {
          show: true,
          color: '#ffffff',
          fontSize: 10,
        },
        itemStyle: {
          color: theme?.primaryColor || '#3B82F6',
        },
        data: periodsData.map((p) => p.idNightCount),
      },
      {
        name: '手机号申请次数',
        type: 'bar',
        label: {
          show: true,
          color: '#ffffff',
          fontSize: 10,
        },
        itemStyle: {
          color: getThemeSecondaryColor(theme?.type),
        },
        data: periodsData.map((p) => p.cellNightCount),
      },
    ],
  }

  return (
    <div className="mt-4">
      <Stack gap="lg">
        <div className="flex flex-col gap-2">
          <Text size="sm" fw={600} style={{ color: theme.accentTextColor }}>
            特殊时段申请分析
          </Text>
          <Text size="xs" className="text-gray-500">
            对比按身份证号和手机号两种维度的周末和夜间（22:00-06:00）申请行为。
          </Text>
        </div>

        <div>
          <Text size="sm" fw={600} mb="xs" style={{ color: theme.accentTextColor }}>
            周末申请统计
          </Text>
          {viewMode === 'table' ? (
            <Table
              data={weekendTableData}
              highlightOnHover
              verticalSpacing="sm"
              styles={{
                table: { fontSize: '13px' },
                th: {
                  backgroundColor: theme.surfaceMutedColor,
                  color: theme.accentTextColor,
                },
              }}
            />
          ) : (
            <ReactECharts option={weekendOptions} style={{ height: '300px' }} />
          )}
        </div>

        <div>
          <Text size="sm" fw={600} mb="xs" style={{ color: theme.accentTextColor }}>
            夜间申请统计
          </Text>
          {viewMode === 'table' ? (
            <Table
              data={nightTableData}
              highlightOnHover
              verticalSpacing="sm"
              styles={{
                table: { fontSize: '13px' },
                th: {
                  backgroundColor: theme.surfaceMutedColor,
                  color: theme.accentTextColor,
                },
              }}
            />
          ) : (
            <ReactECharts option={nightOptions} style={{ height: '300px' }} />
          )}
        </div>
      </Stack>
    </div>
  )
}
